'use client';

import React from 'react';

import { SectionHeader, type SectionHeaderProps } from '@layout';

export type CardHeaderAs = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/** `header` may be a React node, or a plain object of props passed to `SectionHeader`. */
export type CardHeader = React.ReactNode | SectionHeaderProps;

function isSectionHeaderPropsObject(value: CardHeader | undefined): value is SectionHeaderProps {
  return (
    value != null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !React.isValidElement(value)
  );
}

export interface CardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /**
   * Optional card header: React node (string, element, etc.), or a plain object
   * spread onto `SectionHeader` (e.g. `{ title, description, as: 'h3' }`).
   * Same as `title`; if both are set, `header` wins.
   */
  header?: CardHeader;
  /** Same as `header` (whichever the parent prefers). */
  title?: CardHeader;
  /**
   * Extra classes merged into `SectionHeader`’s `headerClassName` when the card header
   * (from `header` or `title`) is an object.
   * `card-header` is always prepended so the header uses the card header base class.
   */
  headerClasses?: string;
  /** Optional heading element when the header is plain text (h1–h6). Same as `as`. */
  headerType?: CardHeaderAs;
  /** Optional heading element when the header is plain text (h1–h6). Same as `headerType`. */
  as?: CardHeaderAs;
  className?: string;
  children: React.ReactNode;
}

/**
 * Card component styled with the `.card` class. Header is optional. Accepts additional className for variants.
 */

const Card = React.forwardRef<HTMLDivElement, CardProps>((props, ref) => {
  const {
    header,
    title,
    headerClasses = '',
    headerType,
    as,
    className = '',
    children,
    ...rest
  } = props;
  const resolvedHeader: CardHeader | undefined = header ?? title;
  const headerAs: CardHeaderAs | undefined = as ?? headerType;

  // If a heading tag is provided, apply headerClasses to the heading element itself.
  const headerContainerClasses = `card-header${!headerAs && headerClasses ? ' ' + headerClasses : ''}`;

  const renderHeader = () => {
    if (!resolvedHeader) return null;
    if (isSectionHeaderPropsObject(resolvedHeader)) {
      const mergedHeaderClassName = ['card-header', headerClasses, resolvedHeader.headerClassName]
        .filter(Boolean)
        .join(' ')
        .trim();
      return <SectionHeader {...resolvedHeader} headerClassName={mergedHeaderClassName} />;
    }
    if (headerAs && (typeof resolvedHeader === 'string' || typeof resolvedHeader === 'number')) {
      return React.createElement(
        headerAs,
        headerClasses ? { className: headerClasses } : null,
        resolvedHeader
      );
    }
    return resolvedHeader;
  };

  const headerUsesSectionHeader = isSectionHeaderPropsObject(resolvedHeader);

  return (
    <div ref={ref} className={`card${className ? ' ' + className : ''}`} {...rest}>
      {resolvedHeader &&
        (headerUsesSectionHeader ? (
          renderHeader()
        ) : (
          <div className={headerContainerClasses}>{renderHeader()}</div>
        ))}

      {children}
    </div>
  );
});
Card.displayName = 'Card';

export default Card;
